import { useEffect, useState, useRef } from "react";
import AdminService from "../../../../service/AdminService";
import ProductService from "../../../../service/ProductService";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { formatDate } from "../../../../utilities/formatDate";
import { Calendar } from "primereact/calendar";
import { FileUpload } from "primereact/fileupload";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { resolveThemeImageUrl } from "../../../../utilities/resolveThemeImageUrl";

const WhitelabelUsers = () => {
  const adminService = new AdminService();
  const productService = new ProductService();

  const [whitelabelUsers, setWhitelabelUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [displayDialog, setDisplayDialog] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [calendarDate, setCalendarDate] = useState("");
  const [displayCustomizeDialog, setDisplayCustomizeDialog] = useState(false);
  const [selectedSubnet, setSelectedSubnet] = useState(null);
  const [displayEditDialog, setDisplayEditDialog] = useState(false);
  const [editSubnetTarget, setEditSubnetTarget] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [logoImage, setLogoImage] = useState(null);
  const [faviconImage, setFaviconImage] = useState(null);
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [showFaviconCropper, setShowFaviconCropper] = useState(false);
  const [loadingCurrentTheme, setLoadingCurrentTheme] = useState(false);
  const [footerAddress, setFooterAddress] = useState("");
  const [footerEmail, setFooterEmail] = useState("");
  const [footerTelephone, setFooterTelephone] = useState("");
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);

  const toast = useRef(null);
  const logoCropperRef = useRef(null);
  const faviconCropperRef = useRef(null);
  const logoUploadRef = useRef(null);
  const faviconUploadRef = useRef(null);

  useEffect(() => {
    let cancel = false;

    fetchWhitelabelUsers(cancel);

    return () => {
      cancel = true;
    };
  }, []);

  async function fetchWhitelabelUsers(cancel) {
    try {
      setLoading(true);

      const response = await adminService.getAllSubnets(
        localStorage.getItem("__extraconomy_token"),
      );

      if (cancel) return;

      if (response.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            localeText("data").find((msg) => msg.code === response.messageCode)?.message ||
            `${localeText("error")}: ${response.messageCode}`,
          life: 6000,
        });
        setLoading(false);
      } else {
        const users = response.result || [];

        setWhitelabelUsers(users);
        setFilteredUsers(users);
        setLoading(false);
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      console.log(e);
      setLoading(false);
    }
  }

  async function fetchManagers() {
    try {
      const response = await adminService.getAreaManagers(
        localStorage.getItem("__extraconomy_token"),
      );

      if (response.messageCode === 200001) {
        const managersList = response.result.userListJSONs.map((user) => ({
          label: `${user.username} (${user.email})`,
          value: user.username,
          username: user.username,
          email: user.email,
          name: `${user.firstname || ""} ${user.surname || ""}`.trim(),
        }));
        setManagers(managersList);
      }
    } catch (e) {
      console.log(e);
    }
  }

  async function createWhitelabelUser() {
    if (!name || name.trim() === "") {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: localeText("pleaseEnterName"),
        life: 6000,
      });
      return;
    }

    if (!description || description.trim() === "") {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: localeText("pleaseEnterDescription"),
        life: 6000,
      });

      return;
    }

    if (!selectedManager) {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: localeText("pleaseSelectManager"),
        life: 6000,
      });

      return;
    }

    try {
      setLoading(true);

      const response = await adminService.createSubnet(
        localStorage.getItem("__extraconomy_token"),
        name.trim(),
        description.trim(),
        selectedManager,
      );

      if (response.messageCode !== 200001) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            localeText("data").find((msg) => msg.code === response.messageCode)?.message ||
            `${localeText("error")}: ${response.messageCode}`,
          life: 6000,
        });

        setLoading(false);
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: localeText("whitelabelSubnetCreatedSuccessfully"),
          life: 6000,
        });

        setDisplayDialog(false);
        resetForm();
        fetchWhitelabelUsers(false);
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }

      console.log(e);

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: e.response?.data?.message || localeText("errorCreatingWhitelabelUser"),
        life: 6000,
      });

      setLoading(false);
    }
  }

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedManager(null);
  };

  const resetCustomizeForm = () => {
    setLogoFile(null);
    setFaviconFile(null);
    setLogoPreview(null);
    setFaviconPreview(null);
    setLogoImage(null);
    setFaviconImage(null);
    setShowLogoCropper(false);
    setShowFaviconCropper(false);
    setFooterAddress("");
    setFooterEmail("");
    setFooterTelephone("");
    setCurrentLogoUrl(null);
    setCurrentFaviconUrl(null);
    setSelectedSubnet(null);
    if (logoUploadRef.current) {
      logoUploadRef.current.clear();
    }
    if (faviconUploadRef.current) {
      faviconUploadRef.current.clear();
    }
  };

  function dataURLtoFile(dataurl, filename) {
    let arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const onLogoChange = (e) => {
    const files = Array.from(e.files);
    const reader = new FileReader();

    reader.onload = () => {
      setLogoImage(reader.result);
      setShowLogoCropper(true);
    };

    reader.readAsDataURL(files[0]);
  };

  const getCroppedLogo = () => {
    if (logoCropperRef.current) {
      const croppedCanvas = logoCropperRef.current.cropper.getCroppedCanvas();
      const croppedImageBase64 = croppedCanvas.toDataURL();

      const img = new Image();
      img.src = croppedImageBase64;
      img.onload = function () {
        const croppedImageFile = dataURLtoFile(croppedImageBase64, "logo.png");
        setLogoFile(croppedImageFile);
        setLogoPreview(croppedImageBase64);
        setShowLogoCropper(false);
      };
    }
  };

  const onFaviconChange = (e) => {
    const files = Array.from(e.files);
    const reader = new FileReader();

    reader.onload = () => {
      setFaviconImage(reader.result);
      setShowFaviconCropper(true);
    };

    reader.readAsDataURL(files[0]);
  };

  const getCroppedFavicon = () => {
    if (faviconCropperRef.current) {
      const croppedCanvas = faviconCropperRef.current.cropper.getCroppedCanvas();
      const croppedImageBase64 = croppedCanvas.toDataURL();

      const img = new Image();
      img.src = croppedImageBase64;
      img.onload = function () {
        const croppedImageFile = dataURLtoFile(croppedImageBase64, "favicon.png");
        setFaviconFile(croppedImageFile);
        setFaviconPreview(croppedImageBase64);
        setShowFaviconCropper(false);
      };
    }
  };

  async function fetchCurrentTheme(subnet) {
    try {
      setLoadingCurrentTheme(true);

      const response = await adminService.getCurrentTheme(
        localStorage.getItem("__extraconomy_token"),
        subnet?.id,
        subnet?.username,
      );

      if (response?.messageCode && response.messageCode !== 200000) {
        toast.current.show({
          severity: "warn",
          summary: localeText("warning"),
          detail:
            localeText("data").find((msg) => msg.code === response.messageCode)?.message ||
            `${localeText("warning")}: ${response.messageCode}`,
          life: 6000,
        });

        return;
      }

      const themeResult = response?.result;

      const rawLogo = themeResult?.themeLogo;
      const rawFavicon = themeResult?.themeFavicon;
      const rawFooterAddress = themeResult?.ownerAddress;
      const rawFooterEmail = themeResult?.ownerEmail;
      const rawFooterTelephone = themeResult?.ownerTelephone;

      setCurrentLogoUrl(rawLogo || null);
      setCurrentFaviconUrl(rawFavicon || null);
      setLogoPreview(resolveThemeImageUrl(rawLogo) || rawLogo || null);
      setFaviconPreview(resolveThemeImageUrl(rawFavicon) || rawFavicon || null);
      setFooterAddress(rawFooterAddress);
      setFooterEmail(rawFooterEmail);
      setFooterTelephone(rawFooterTelephone);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }

      console.log(e);

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: e.response?.data?.message || "Erro ao carregar o tema atual",
        life: 6000,
      });
    } finally {
      setLoadingCurrentTheme(false);
    }
  }

  const handleOpenCustomizeDialog = async (subnet) => {
    resetCustomizeForm();
    setSelectedSubnet(subnet);
    setDisplayCustomizeDialog(true);
    await fetchCurrentTheme(subnet);
  };

  const handleOpenEditDialog = (subnet) => {
    setEditSubnetTarget(subnet);
    setEditName(subnet.name || "");
    setEditDescription(subnet.description || "");
    setDisplayEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setDisplayEditDialog(false);
    setEditSubnetTarget(null);
    setEditName("");
    setEditDescription("");
  };

  async function saveSubnetEdit() {
    if (!editName.trim()) {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: localeText("pleaseEnterName"),
        life: 6000,
      });
      return;
    }
    if (!editDescription.trim()) {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: localeText("pleaseEnterDescription"),
        life: 6000,
      });
      return;
    }

    try {
      setSavingEdit(true);
      const response = await adminService.editSubnet(
        localStorage.getItem("__extraconomy_token"),
        editSubnetTarget.id,
        editName.trim(),
        editDescription.trim(),
      );

      if (response.messageCode !== 200001 && response.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            localeText("data").find((msg) => msg.code === response.messageCode)?.message ||
            `${localeText("error")}: ${response.messageCode}`,
          life: 6000,
        });
        return;
      }

      toast.current.show({
        severity: "success",
        summary: localeText("success"),
        detail: localeText("subnetEditedSuccessfully") || "Sub-rede editada com sucesso!",
        life: 6000,
      });
      handleCloseEditDialog();
      fetchWhitelabelUsers(false);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: e.response?.data?.message || "Erro ao editar sub-rede",
        life: 6000,
      });
    } finally {
      setSavingEdit(false);
    }
  }

  const handleImageUpload = async (image) => {
    try {
      const res = await productService.postUploadImage(
        localStorage.getItem("__extraconomy_token"),
        image,
      );

      if (res.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            localeText("data").find((msg) => msg.code === res.messageCode)?.message ||
            `${localeText("error")}: ${res.messageCode}`,
          life: 6000,
        });
        return null;
      }

      return res.result;
    } catch (e) {
      console.log(e);
      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: "Erro ao fazer upload da imagem",
        life: 6000,
      });
      return null;
    }
  };

  async function customizeSubnetTheme() {
    if (
      !logoFile &&
      !faviconFile &&
      !footerAddress.trim() &&
      !footerEmail.trim() &&
      !footerTelephone.trim()
    ) {
      toast.current.show({
        severity: "warn",
        summary: localeText("warning"),
        detail: "Por favor, informe ao menos um campo para personalização",
        life: 6000,
      });
      return;
    }

    try {
      setLoading(true);

      let logoUrl = null;
      let faviconUrl = null;

      if (logoFile) {
        toast.current.show({
          severity: "info",
          summary: localeText("info") || "Info",
          detail: "Fazendo upload da logo...",
          life: 3000,
        });
        logoUrl = await handleImageUpload(logoFile);
        if (!logoUrl) {
          setLoading(false);
          return;
        }
      }

      if (faviconFile) {
        toast.current.show({
          severity: "info",
          summary: localeText("info") || "Info",
          detail: "Fazendo upload do favicon...",
          life: 3000,
        });
        faviconUrl = await handleImageUpload(faviconFile);
        if (!faviconUrl) {
          setLoading(false);
          return;
        }
      }

      const finalLogoUrl = logoUrl || currentLogoUrl;
      const finalFaviconUrl = faviconUrl || currentFaviconUrl;

      const payload = {
        subnetId: selectedSubnet.id,
        username: selectedSubnet.username,
        footerAddress: footerAddress.trim(),
        footerEmail: footerEmail.trim(),
        footerTelephone: footerTelephone.trim(),
      };

      if (finalLogoUrl) {
        payload.logoUrl = finalLogoUrl;
      }

      if (finalFaviconUrl) {
        payload.faviconUrl = finalFaviconUrl;
      }

      const response = await adminService.customizeTheme(
        localStorage.getItem("__extraconomy_token"),
        payload,
      );

      if (response.messageCode !== 200001 && response.messageCode !== 200000) {
        toast.current.show({
          severity: "error",
          summary: localeText("error"),
          detail:
            localeText("data").find((msg) => msg.code === response.messageCode)?.message ||
            `${localeText("error")}: ${response.messageCode}`,
          life: 6000,
        });
        setLoading(false);
      } else {
        toast.current.show({
          severity: "success",
          summary: localeText("success"),
          detail: "Tema personalizado com sucesso!",
          life: 6000,
        });

        setDisplayCustomizeDialog(false);
        resetCustomizeForm();
        setLoading(false);
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }

      console.log(e);

      toast.current.show({
        severity: "error",
        summary: localeText("error"),
        detail: e.response?.data?.message || "Erro ao personalizar tema",
        life: 6000,
      });

      setLoading(false);
    }
  }

  const handleOpenDialog = () => {
    setDisplayDialog(true);
    fetchManagers();
  };

  const convertDateArrayToDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return null;

    return new Date(
      dateArray[0],
      dateArray[1] - 1,
      dateArray[2],
      dateArray[3],
      dateArray[4],
      dateArray[5],
    );
  };

  const filters2 = {
    id: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ownerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    memberCount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dateCreation: { value: null, matchMode: FilterMatchMode.DATE_IS },
  };

  const filterByDate = (date) => {
    if (date) {
      const filteredData = whitelabelUsers.filter((obj) => {
        const objDate = convertDateArrayToDate(obj?.createdDate);
        return objDate && formatDate(objDate) === formatDate(date);
      });
      setFilteredUsers([...filteredData]);
    } else {
      setFilteredUsers(whitelabelUsers);
    }
  };

  const dateFilterTemplate = (options) => {
    return (
      <div className="flex justify-content-center align-items-center">
        <Calendar
          value={calendarDate}
          onChange={(e) => {
            setCalendarDate(e.value);
            filterByDate(new Date(e.value));
          }}
          dateFormat="dd/mm/yy"
          placeholder="Selecione uma data"
          showIcon
        />
        <Button
          icon="pi pi-times"
          size="small"
          className="p-button-danger p-button-rounded p-button-text"
          onClick={() => {
            setCalendarDate("");
            setFilteredUsers(whitelabelUsers);
          }}
        />
      </div>
    );
  };

  const dialogFooter = (
    <div>
      <Button
        label={localeText("cancel")}
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          setDisplayDialog(false);
          resetForm();
        }}
      />
      <Button
        label={localeText("confirm")}
        icon="pi pi-check"
        className="p-button-success"
        onClick={createWhitelabelUser}
        disabled={loading}
      />
    </div>
  );

  const customizeDialogFooter = (
    <div>
      <Button
        label={localeText("cancel")}
        icon="pi pi-times"
        className="p-button-success"
        onClick={() => {
          setDisplayCustomizeDialog(false);
          resetCustomizeForm();
        }}
      />
      <Button
        label={localeText("confirm")}
        icon="pi pi-check"
        className="p-button-success"
        onClick={customizeSubnetTheme}
        disabled={loading}
      />
    </div>
  );

  return (
    <div className="card border-1 border-300 border-round">
      <div className="flex justify-content-between align-items-center my-4">
        <h3>Whitelabel {localeText("subnets")}</h3>
        <Button
          label={`${localeText("add")} ${localeText("subnet")}`}
          icon="pi pi-plus"
          className="p-button-success"
          onClick={handleOpenDialog}
        />
      </div>
      <hr />

      <Dialog
        header={localeText("addWhitelabelUser") || "Adicionar Usuário Whitelabel"}
        visible={displayDialog}
        style={{ width: "450px" }}
        footer={dialogFooter}
        onHide={() => {
          setDisplayDialog(false);
          resetForm();
        }}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="name">{localeText("name") || "Nome"}</label>
            <InputText
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={localeText("enterName") || "Digite o nome"}
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="description">{localeText("description") || "Descrição"}</label>
            <InputText
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={localeText("enterDescription") || "Digite a descrição"}
            />
          </div>

          <div className="field">
            <label htmlFor="manager">{localeText("manager") || "Manager"}</label>
            <Dropdown
              id="manager"
              value={selectedManager}
              options={managers}
              onChange={(e) => setSelectedManager(e.value)}
              placeholder={localeText("selectManager") || "Selecione um manager"}
              filter
              filterBy="label"
              showClear
              emptyMessage={localeText("noManagersFound") || "Nenhum manager encontrado"}
            />
            <small className="p-text-secondary">
              {localeText("searchByUsernameNameOrEmail") || "Busque por username, nome ou email"}
            </small>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={`${localeText("personalizeTeam")} - ${selectedSubnet?.name || "Subnet"}`}
        visible={displayCustomizeDialog}
        style={{ width: "550px" }}
        footer={customizeDialogFooter}
        draggable={false}
        onHide={() => {
          setDisplayCustomizeDialog(false);
          resetCustomizeForm();
        }}
      >
        <div className="p-fluid">
          {loadingCurrentTheme && (
            <div className="flex align-items-center gap-2 mb-3">
              <i className="pi pi-spin pi-spinner" />
              <small className="p-text-secondary">{localeText("loading")}</small>
            </div>
          )}

          <div className="field">
            <label htmlFor="logo">
              Logo <small className="p-text-secondary">(Aspect Ratio 16:4)</small>
            </label>
            <FileUpload
              ref={logoUploadRef}
              name="logo"
              accept="image/*"
              maxFileSize={5000000}
              onSelect={onLogoChange}
              onClear={() => {
                setLogoFile(null);
                setLogoPreview(null);
                setLogoImage(null);
                setShowLogoCropper(false);
              }}
              chooseLabel={localeText("choose") || "Escolher"}
              cancelLabel={localeText("cancel") || "Cancelar"}
              emptyTemplate={
                <div className="text-center p-3">
                  <i className="pi pi-image text-400" style={{ fontSize: "2em" }}></i>
                  <p className="text-400 mt-2">
                    {localeText("dragImgFile") || "Arraste uma imagem aqui"}
                  </p>
                </div>
              }
              auto={false}
              customUpload
              uploadOptions={{ style: { display: "none" } }}
            />

            {showLogoCropper && (
              <div className="mt-3">
                <Cropper
                  ref={logoCropperRef}
                  src={logoImage}
                  style={{ height: 300, width: "100%", marginBottom: 10 }}
                  aspectRatio={4 / 1}
                  guides={true}
                  viewMode={1}
                />
                <Button
                  label={localeText("cropImage") || "Recortar Imagem"}
                  icon="pi pi-check"
                  onClick={getCroppedLogo}
                  className="p-button-success w-full"
                />
              </div>
            )}

            {logoPreview && !showLogoCropper && (
              <div className="mt-3 text-center">
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  style={{ maxWidth: "100%", maxHeight: "150px", objectFit: "contain" }}
                />
              </div>
            )}
          </div>

          <div className="field">
            <label htmlFor="favicon">
              Favicon <small className="p-text-secondary">(Aspect Ratio 1:1 - Quadrado)</small>
            </label>
            <FileUpload
              ref={faviconUploadRef}
              name="favicon"
              accept="image/*"
              maxFileSize={5000000}
              onSelect={onFaviconChange}
              onClear={() => {
                setFaviconFile(null);
                setFaviconPreview(null);
                setFaviconImage(null);
                setShowFaviconCropper(false);
              }}
              chooseLabel={localeText("choose") || "Escolher"}
              cancelLabel={localeText("cancel") || "Cancelar"}
              emptyTemplate={
                <div className="text-center p-3">
                  <i className="pi pi-image text-400" style={{ fontSize: "2em" }}></i>
                  <p className="text-400 mt-2">
                    {localeText("dragImgFile") || "Arraste uma imagem aqui"}
                  </p>
                </div>
              }
              auto={false}
              customUpload
              uploadOptions={{ style: { display: "none" } }}
            />

            {showFaviconCropper && (
              <div className="mt-3">
                <Cropper
                  ref={faviconCropperRef}
                  src={faviconImage}
                  style={{ height: 300, width: "100%", marginBottom: 10 }}
                  aspectRatio={1 / 1}
                  guides={true}
                  viewMode={1}
                />
                <Button
                  label={localeText("cropImage") || "Recortar Imagem"}
                  icon="pi pi-check"
                  onClick={getCroppedFavicon}
                  className="p-button-success w-full"
                />
              </div>
            )}

            {faviconPreview && !showFaviconCropper && (
              <div className="mt-3 text-center">
                <img
                  src={faviconPreview}
                  alt="Favicon Preview"
                  style={{ maxWidth: "100px", maxHeight: "100px", objectFit: "contain" }}
                />
              </div>
            )}
          </div>

          <small className="p-text-secondary">* {localeText("footerFieldsInfo")}</small>

          <div className="field">
            <label htmlFor="footerAddress">{localeText("address")} (footer)</label>
            <InputText
              id="footerAddress"
              value={footerAddress}
              onChange={(e) => setFooterAddress(e.target.value)}
              placeholder="Digite o endereço"
            />
          </div>

          <div className="field">
            <label htmlFor="footerEmail">{localeText("email")} (footer)</label>
            <InputText
              id="footerEmail"
              type="email"
              value={footerEmail}
              onChange={(e) => setFooterEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="footerTelephone">{localeText("phone")} (footer)</label>
            <InputText
              id="footerTelephone"
              type="tel"
              value={footerTelephone}
              onChange={(e) => setFooterTelephone(e.target.value)}
            />
          </div>

          <small className="p-text-secondary">* {localeText("logoOrFavicon")}</small>
        </div>
      </Dialog>

      <Dialog
        header={`${localeText("editSubnetData") || "Editar dados da sub-rede"} - ${editSubnetTarget?.name || ""}`}
        visible={displayEditDialog}
        style={{ width: "450px" }}
        draggable={false}
        footer={
          <div>
            <Button
              label={localeText("cancel")}
              icon="pi pi-times"
              className="p-button-text"
              onClick={handleCloseEditDialog}
              disabled={savingEdit}
            />
            <Button
              label={localeText("save")}
              icon="pi pi-check"
              className="p-button-success"
              onClick={saveSubnetEdit}
              loading={savingEdit}
            />
          </div>
        }
        onHide={handleCloseEditDialog}
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="editName">{localeText("name") || "Nome"}</label>
            <InputText
              id="editName"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="field">
            <label htmlFor="editDescription">{localeText("description") || "Descrição"}</label>
            <InputText
              id="editDescription"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </div>
        </div>
      </Dialog>

      <Toast ref={toast} />
      <DataTable
        value={filteredUsers}
        paginator
        responsiveLayout="scroll"
        loading={loading}
        filters={filters2}
        filterDisplay="row"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate={localeText("showingFirstToLast")}
        rows={10}
        rowsPerPageOptions={[10, 20, 50]}
        emptyMessage={
          localeText("noWhitelabelUsersFound") || "Nenhum usuário Whitelabel encontrado"
        }
      >
        <Column
          field="id"
          header={"Id"}
          filter
          filterPlaceholder={localeText("searchById")}
          filters={filters2}
          filterDisplay="row"
          sortable
        />
        <Column
          field="name"
          header={localeText("name")}
          filter
          filterPlaceholder={localeText("searchByName")}
          filters={filters2}
          filterDisplay="row"
          sortable
        />
        <Column
          field="description"
          header={localeText("description")}
          filter
          filterPlaceholder={localeText("searchByDescription")}
          filters={filters2}
          filterDisplay="row"
          sortable
        />
        <Column
          field="ownerName"
          header={localeText("ownerName")}
          filter
          filterPlaceholder={localeText("searchByOwnerName")}
          filters={filters2}
          filterDisplay="row"
          sortable
        />
        <Column
          field="memberCount"
          header={localeText("memberCount")}
          filter
          filterPlaceholder={localeText("searchByMemberCount")}
          filters={filters2}
          filterDisplay="row"
          sortable
        />
        <Column
          header={localeText("adminBackofficeDateCreation")}
          field="createdDate"
          filter
          dataType="date"
          filterElement={dateFilterTemplate}
          body={(user) => {
            const date = convertDateArrayToDate(user.createdDate);
            return date ? formatDate(date) : "-";
          }}
          sortable
          showFilterMenu={false}
        />
        <Column
          field="active"
          header={localeText("active")}
          body={(user) => {
            return (
              <>
                {user.active ? (
                  <span
                    className="team-member-role text-0 p-1 border-round"
                    style={{ backgroundColor: "#46a748" }}
                  >
                    {localeText("yes").toUpperCase()}
                  </span>
                ) : (
                  <span
                    className="team-member-role text-0 p-1 border-round"
                    style={{ backgroundColor: "#eb4034" }}
                  >
                    {localeText("no").toUpperCase()}
                  </span>
                )}
              </>
            );
          }}
          sortable
        />

        <Column
          header={localeText("actions") || "Ações"}
          exportable={false}
          style={{ minWidth: "8rem" }}
          body={(rowData) => {
            return (
              <div className="flex gap-2">
                <Button
                  icon="pi pi-palette"
                  className="p-button-rounded p-button-success p-button-sm"
                  onClick={() => handleOpenCustomizeDialog(rowData)}
                  title={localeText("personalizeSubnetTheme")}
                />
                <Button
                  icon="pi pi-pencil"
                  className="p-button-rounded p-button-sm"
                  style={{ backgroundColor: "#3B82F6", borderColor: "#3B82F6" }}
                  onClick={() => handleOpenEditDialog(rowData)}
                  title={localeText("editSubnetData") || "Editar dados da sub-rede"}
                />
              </div>
            );
          }}
        />
      </DataTable>
    </div>
  );
};

export default WhitelabelUsers;
